import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Typography,
  Button,
  Form,
  Select,
  Input,
  message,
  Row,
  Col,
  Alert,
  DatePicker,
  Spin,
  Divider,
  Tag,
} from "antd";
import { ArrowLeftOutlined, SafetyOutlined, AlertOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { AppContext } from "../../context/AppContext";
import moment from "moment";
import { LineWave } from "react-loader-spinner";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const UpdateLoanStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { company, user } = useContext(AppContext);
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  // Define loan status options with descriptions
  const loanStatusOptions = [
    {
      value: 1,
      label: "Pending Approval",
      description: "Loan application is waiting for review and approval.",
      color: "orange",
    },
    {
      value: 2,
      label: "Under Review",
      description: "Loan is currently being evaluated by the loan committee.",
      color: "blue",
    },
    {
      value: 3,
      label: "Incomplete Application",
      description: "Additional information or documents are required.",
      color: "gold",
    },
    {
      value: 7,
      label: "Active",
      description: "Loan has been approved and is currently active.",
      color: "green",
    },
    {
      value: 8,
      label: "Overdue",
      description: "Loan has missed one or more payment deadlines.",
      color: "red",
    },
    {
      value: 9,
      label: "Defaulted",
      description: "Loan is in severe delinquency status.",
      color: "volcano",
    },
    {
      value: 10,
      label: "Closed",
      description: "Loan has been fully repaid or written off.",
      color: "gray",
    },
    {
      value: 12,
      label: "App Application",
      description: "Submitted by the borrower through the mobile app, awaiting review.",
      color: "purple",
    },
  ];

  // Get loan status label by value
  const getLoanStatusLabel = (value) => {
    const status = loanStatusOptions.find(option => option.value === value);
    return status ? status.label : "Unknown";
  };

  // Get loan status color by value
  const getLoanStatusColor = (value) => {
    const status = loanStatusOptions.find(option => option.value === value);
    return status ? status.color : "default";
  };

  // Fetch loan details on component mount
  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans/${id}?fields=*,borrower.id,borrower.first_name,borrower.last_name`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        setLoan(response.data.data);
        // Set the initial form values
        form.setFieldsValue({
          loan_status: response.data.data.loan_status,
          notes: response.data.data.status_notes || "",
          next_payment_date: response.data.data.next_payment_date ? moment(response.data.data.next_payment_date) : null,
        });
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch loan details");
      } finally {
        setLoading(false);
      }
    };

    fetchLoanDetails();
  }, [id, form, token]);

  const handleStatusChange = async (values) => {
    try {
      setSubmitting(true);

      if (
        values.loan_status === 8 &&
        values.next_payment_date &&
        values.next_payment_date.isSameOrAfter(moment(), "day")
      ) {
        values.loan_status = 7;
      }

      // Format dates for API
      const formattedValues = {
        ...values,
        next_payment_date: values.next_payment_date ? values.next_payment_date.format('YYYY-MM-DD') : null,
        status_update_date: moment().format('YYYY-MM-DD'),
        status_updated_by: user.id,
      };
      
      // Create a status update log entry if needed
      if (loan.loan_status !== values.loan_status) {
        try {
          await axios.post(
            `https://zedlenders.pickmesms.com/items/loan_status_logs`,
            {
              loan_id: id,
              previous_status: loan.loan_status,
              new_status: values.loan_status,
              updated_by: user.id,
              update_date: moment().format('YYYY-MM-DD'),
              notes: values.notes,
              company: company?.id,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } catch (logError) {
          console.error("Error creating status log:", logError);
          // Continue with update even if log fails
        }
      }
      
      // Update the loan status
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${id}`,
        formattedValues,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      message.success("Loan status updated successfully");
      
      // Redirect back to loan details page
      navigate(`/loan/${id}`);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update loan status");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <LineWave
          visible={true}
          height="100"
          width="100"
          color="#36454F"
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Alert 
            message="Error Loading Loan" 
            description={error}
            type="error"
            showIcon
          />
          <Button 
            type="primary" 
            onClick={() => navigate(-1)} 
            style={{ marginTop: 16 }}
          >
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Col xs={24} sm={16} md={16} lg={12} xl={12} style={{ margin: "0 auto" }}>
      <Card>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/loan/${id}`)}
          style={{ marginBottom: 16, padding: 0 }}
        >
          Back to Loan Details
        </Button>

        <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
          Update Loan Status
        </Title>

        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <Text>Loan ID: {loan.id}</Text>
          <br />
          <Text strong>
            Borrower: {loan.borrower.first_name} {loan.borrower.last_name}
          </Text>
          <br />
          <Text>Amount: ZMW {loan.amount.toLocaleString()}</Text>
          <br />
          <Text>
            Current Status: <Tag color={getLoanStatusColor(loan.loan_status)}>{getLoanStatusLabel(loan.loan_status)}</Tag>
          </Text>
        </div>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleStatusChange}
        >
          <Form.Item
            name="loan_status"
            label="Status"
            rules={[{ required: true, message: "Please select the loan status" }]}
          >
            <Select>
              {loanStatusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag> - {option.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="next_payment_date"
            label="Next Payment Date"
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder="Select next payment date if applicable"
              onChange={(date) => {
                const currentStatus = form.getFieldValue("loan_status");
                if (
                  currentStatus === 8 &&
                  date &&
                  date.isSameOrAfter(moment(), "day")
                ) {
                  form.setFieldsValue({ loan_status: 7 });
                  message.info(
                    "Status changed to Active because the next payment date is no longer overdue."
                  );
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Status Notes"
          >
            <TextArea
              rows={4}
              placeholder="Add notes explaining the status change"
            />
          </Form.Item>
          
          <Alert
            message="Status Update Information"
            description={
              <div>
                <p>Updating the loan status will:</p>
                <ul>
                  <li>Change the current status of the loan</li>
                  <li>Create an entry in the status history log</li>
                  <li>Notify relevant staff about this change</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            icon={<SafetyOutlined />}
            style={{ marginBottom: 24 }}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              style={{ height: 40 }}
            >
              {submitting ? "Updating..." : "Update Loan Status"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Col>
  );
};

export default UpdateLoanStatus;